/*
 * Express-Personalisierung cart invariant:
 * - zero Express lines when the cart has no regular product
 * - exactly one Express line with quantity one otherwise
 *
 * Liquid remains the source of truth for the visible toggle state. This controller only repairs
 * invalid Ajax cart states and then renders Shopify's returned sections.
 */
(() => {
  const EXPRESS_PROPERTY = '_petamor_express';
  const DEFAULT_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  let reconciliationPromise;
  let toggleMutationQueue = Promise.resolve();

  function enqueueToggleMutation(operation) {
    const queuedOperation = toggleMutationQueue.catch(() => undefined).then(operation);
    toggleMutationQueue = queuedOperation;
    return queuedOperation;
  }

  function configuredVariantId() {
    return document.querySelector('cart-drawer')?.dataset.expressVariant || '';
  }

  function hasExpressMarker(item) {
    return String(item?.properties?.[EXPRESS_PROPERTY]).toLowerCase() === 'true';
  }

  function isExpressItem(item, variantId = configuredVariantId()) {
    const matchesVariant = variantId && String(item?.variant_id) === String(variantId);
    return Boolean(hasExpressMarker(item) || matchesVariant);
  }

  function isCompleteCartData(cartData) {
    return (
      Array.isArray(cartData?.items) &&
      typeof cartData.item_count === 'number' &&
      typeof cartData.total_price === 'number'
    );
  }

  function sectionsToRender() {
    const sections = [...DEFAULT_SECTIONS];
    const mainItems = document.getElementById?.('main-cart-items');
    const mainFooter = document.getElementById?.('main-cart-footer');

    if (mainItems?.dataset.id) sections.push(mainItems.dataset.id);
    if (mainFooter?.dataset.id) sections.push(mainFooter.dataset.id);
    return [...new Set(sections)];
  }

  function planUpdates(cartData, { forceRemove = false, variantId = configuredVariantId() } = {}) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    const expressItems = items.filter((item) => isExpressItem(item, variantId));
    const regularItems = items.filter((item) => !isExpressItem(item, variantId));
    const updates = {};

    if (!expressItems.length) return updates;

    if (forceRemove || !regularItems.length) {
      expressItems.forEach((item) => {
        updates[item.key] = 0;
      });
      return updates;
    }

    // Prefer the private line created by this controller. A direct product add may be older and
    // lacks the marker needed to clean it up if the configured product changes later.
    const canonicalExpressItem = expressItems.find(hasExpressMarker) || expressItems[0];
    expressItems.forEach((item) => {
      if (item !== canonicalExpressItem) {
        updates[item.key] = 0;
      } else if (Number(item.quantity) !== 1) {
        updates[item.key] = 1;
      }
    });

    return updates;
  }

  async function updateCart(updates, sections = sectionsToRender()) {
    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    const parsed = await response.json();

    if (response.ok === false || parsed.status || parsed.errors) {
      throw new Error(parsed.description || parsed.message || 'Express cart update failed');
    }
    return parsed;
  }

  async function reconcile(cartData, options = {}) {
    const updates = planUpdates(cartData, options);
    if (!Object.keys(updates).length) return cartData;

    if (!reconciliationPromise) {
      reconciliationPromise = updateCart(updates, options.sections).finally(() => {
        reconciliationPromise = undefined;
      });
    }
    return reconciliationPromise;
  }

  function sectionDocument(html) {
    if (!html || typeof DOMParser === 'undefined') return null;
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function renderSections(cartData, { keepDrawerOpen = false } = {}) {
    if (!cartData?.sections) return;

    const drawer = document.querySelector('cart-drawer');
    const drawerWasOpen = Boolean(drawer?.classList.contains('active'));
    let drawerRenderedSections = false;
    if (drawer && cartData.sections['cart-drawer']) {
      if ((keepDrawerOpen || drawerWasOpen) && typeof drawer.renderContents === 'function') {
        drawer.renderContents(cartData);
        // Dawn's CartDrawer.renderContents updates both #CartDrawer and #cart-icon-bubble.
        drawerRenderedSections = true;
      } else {
        const source = sectionDocument(cartData.sections['cart-drawer'])?.querySelector('#CartDrawer');
        const target = drawer.querySelector?.('#CartDrawer');
        if (source && target) {
          target.innerHTML = source.innerHTML;
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        }
      }
    }

    if (!drawerRenderedSections) {
      const bubbleHtml = cartData.sections['cart-icon-bubble'];
      const bubbleTarget = document.getElementById?.('cart-icon-bubble');
      const bubbleSource = sectionDocument(bubbleHtml)?.querySelector('.shopify-section');
      if (bubbleTarget && bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;
    }

    const mainItems = document.getElementById?.('main-cart-items');
    const itemsSectionId = mainItems?.dataset.id;
    const sourceCartItems = sectionDocument(cartData.sections[itemsSectionId])?.querySelector('cart-items');
    const targetCartItems = mainItems?.closest?.('cart-items');
    if (sourceCartItems && targetCartItems) targetCartItems.replaceWith(sourceCartItems);

    const mainFooter = document.getElementById?.('main-cart-footer');
    const footerSectionId = mainFooter?.dataset.id;
    const sourceFooter = sectionDocument(cartData.sections[footerSectionId])?.querySelector('#main-cart-footer');
    if (sourceFooter && mainFooter) mainFooter.replaceWith(sourceFooter);

    if (Array.isArray(cartData.items)) {
      const hasRegularItems = cartData.items.some((item) => !isExpressItem(item));
      drawer?.classList.toggle('is-empty', !hasRegularItems);
    }
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (response.ok === false) throw new Error('Unable to read cart');
    return response.json();
  }

  async function completeCartData(cartData) {
    if (isCompleteCartData(cartData)) return cartData;

    const completeCart = await fetchCart();
    return {
      ...cartData,
      ...completeCart,
      // Keep the section HTML returned by the mutation. /cart.js supplies the canonical cart
      // totals and lines, but it does not render the drawer or cart-icon sections.
      sections: cartData?.sections || completeCart.sections,
    };
  }

  const controller = {
    completeCartData,
    enqueueToggleMutation,
    isCompleteCartData,
    isExpressItem,
    planUpdates,
    reconcile,
    renderSections,
    sectionsToRender,
  };
  window.PetamorExpressCart = controller;

  async function repairPublishedCart(event) {
    if (!event?.cartData || event.source === 'express-reconcile') return;

    try {
      // cart/add.js returns `items`, but only for the lines that were just added. It is not a full
      // cart and must never drive the orphan-removal invariant by itself.
      const cartData = isCompleteCartData(event.cartData) ? event.cartData : await fetchCart();
      const corrected = await reconcile(cartData);
      if (corrected === cartData) return;

      renderSections(corrected);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'express-reconcile', cartData: corrected });
      }
    } catch (error) {
      console.error('Express-Personalisierung could not be normalized.', error);
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, repairPublishedCart);
  }

  async function repairServerRenderedCart() {
    const cartDrawerContent = document.querySelector('#CartDrawer');
    const needsRepair = cartDrawerContent?.dataset.expressNeedsReconcile === 'true';
    if (!needsRepair) return;

    try {
      const cartData = await fetchCart();
      const corrected = await reconcile(cartData, {
        forceRemove: cartDrawerContent.dataset.expressForceRemove === 'true',
      });
      if (corrected === cartData) return;

      renderSections(corrected);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'express-reconcile', cartData: corrected });
      }
    } catch (error) {
      console.error('Express-Personalisierung could not be normalized on page load.', error);
    }
  }

  if (document.readyState === 'loading' && typeof document.addEventListener === 'function') {
    document.addEventListener('DOMContentLoaded', repairServerRenderedCart, { once: true });
  } else {
    repairServerRenderedCart();
  }

  if (!customElements.get('express-toggle')) {
    customElements.define(
      'express-toggle',
      class ExpressToggle extends HTMLElement {
        constructor() {
          super();
          this.input = this.querySelector('input[type="checkbox"]');
          if (this.input) this.input.addEventListener('change', this.onChange.bind(this));
        }

        async onChange() {
          const variantId = this.dataset.variant;
          if (!variantId) return;

          const wasChecked = this.input.checked;
          this.setLoading(true);
          const sections = sectionsToRender();

          return enqueueToggleMutation(async () => {
            try {
              let completeCart;

              if (wasChecked) {
                const body = {
                  items: [{ id: variantId, quantity: 1, properties: { [EXPRESS_PROPERTY]: 'true' } }],
                  sections,
                  sections_url: window.location.pathname,
                };
                const response = await fetch(routes.cart_add_url, {
                  ...fetchConfig(),
                  body: JSON.stringify(body),
                });
                const parsed = await response.json();

                if (response.ok === false || parsed.status || parsed.errors) {
                  throw new Error(parsed.description || parsed.message || 'Express toggle failed');
                }

                completeCart = await completeCartData(parsed);
                completeCart = await reconcile(completeCart, { sections });
              } else {
                // Variant IDs are ambiguous when Shopify has merged the fee quantity or when a
                // stale/direct add produced multiple Express lines. Remove every matching key in
                // one atomic cart/update request.
                const currentCart = await fetchCart();
                const updates = planUpdates(currentCart, { forceRemove: true, variantId });
                completeCart = Object.keys(updates).length ? await updateCart(updates, sections) : currentCart;
              }

              renderSections(completeCart, { keepDrawerOpen: true });
              this.setLoading(false);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                // Dawn cart subscribers can perform their own section request. Await them so their
                // redraw cannot finish later and overwrite this canonical Express state.
                await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'express-toggle', cartData: completeCart });
              }
            } catch (error) {
              this.input.checked = !wasChecked;
              this.setLoading(false);
              console.error('Express-Personalisierung could not be updated.', error);
            }
          });
        }

        setLoading(state) {
          this.classList.toggle('is-loading', state);
          if (this.input) this.input.disabled = state;
        }
      }
    );
  }
})();
